import React from 'react';
import { useAuthStore } from '../../stores/authStore';
import { Login } from './Login';
import { Register } from './Register';
import { useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Input } from '../ui/Input/Input';
import { Button } from '../ui/Button/Button';
import { Alert } from '../ui/Alert/Alert';
import { Checkbox } from '../ui/Checkbox/Checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs/Tabs';
import { apiFetch } from '../../lib/api';
import { Logo } from '../ui/Logo';

export function AuthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [mode, setMode] = React.useState<'login' | 'register'>(tabParam === 'register' ? 'register' : 'login');
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const location = useLocation() as unknown as { state?: { from?: string } };

  // Sync tab with URL param
  const handleTabChange = (v: string) => {
    const tab = v as 'login' | 'register';
    setMode(tab);
    const params = new URLSearchParams(searchParams);
    if (tab === 'register') {
      params.set('tab', 'register');
    } else {
      params.delete('tab');
    }
    setSearchParams(params, { replace: true });
  };

  React.useEffect(() => {
    if (user) {
      
      // Check if we need to redirect to onboarding for new registrations
      const checkOnboarding = async () => {
        try {
          // Check for new registration flag
          const redirectOnboarding = localStorage.getItem('ima_redirect_onboarding') === '1';
          
          
          if (redirectOnboarding) {
            
            localStorage.removeItem('ima_redirect_onboarding');
            navigate('/onboarding', { replace: true });
            return;
          }

          // Otherwise check actual onboarding status
          
          const orgsRes = await apiFetch('/api/orgs');
          if (orgsRes.ok) {
            const payload = await orgsRes.json();
            const orgs = Array.isArray(payload?.data) ? payload.data : [];
            
            
            let activeId: string | null = null;
            try { activeId = localStorage.getItem('ima_active_org_id'); } catch {}
            const active = orgs.find((o: any) => o.id === activeId) || orgs[0];
            
            
            if (active) {
              let completed = Boolean(active.onboarding_completed);
              if (!completed) {
                try { completed = localStorage.getItem(`ima_onboarding_completed_${active.id}`) === '1'; } catch {}
              }
              const destination = completed ? '/dashboard' : '/onboarding';
              navigate(destination, { replace: true });
              return;
            }
          } else if (orgsRes.status === 401) {
            // User is no longer authenticated, clear the store
            useAuthStore.setState({ user: null });
            return;
          }
        } catch (e) {
          
        }
        
        // Fallback
        const from = (location?.state?.from as string | undefined) || '/dashboard';
        
        navigate(from, { replace: true });
      };

      checkOnboarding();
    }
  }, [user, navigate, location]);

  if (user) {
    // Avoid rendering the auth UI at all if already authenticated
    return null;
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block hover:opacity-80 transition-opacity">
            <Logo size="xl" className="justify-center mb-2" />
            <p className="text-sm text-muted-foreground mt-1">Artistic Intelligence</p>
          </Link>
        </div>

        {/* Auth Card */}
        <div className="bg-background rounded-2xl shadow-xl border overflow-hidden">
          <Tabs value={mode} onValueChange={handleTabChange}>
            <TabsList className="w-full rounded-none h-12 p-0">
              <TabsTrigger value="login" className="flex-1 h-full rounded-none">Sign In</TabsTrigger>
              <TabsTrigger value="register" className="flex-1 h-full rounded-none">Create Account</TabsTrigger>
            </TabsList>
            
            <div className="p-8">
              <TabsContent value="login" className="mt-0">
                <LoginForm />
              </TabsContent>
              <TabsContent value="register" className="mt-0">
                <RegisterForm />
              </TabsContent>
            </div>
          </Tabs>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-muted-foreground">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
}

function LoginForm() {
  const login = useAuthStore(s => s.login);
  const isLoading = useAuthStore(s => s.isLoading);
  const error = useAuthStore(s => s.error);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [remember, setRemember] = React.useState(true);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(email, password, remember);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div className="space-y-4">
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="you@example.com"
          required
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="••••••••"
          required
        />
      </div>
      
      <div className="flex items-center justify-between">
        <Checkbox
          checked={remember}
          onChange={e => setRemember(e.target.checked)}
          label="Remember me"
        />
        <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700">Forgot password?</Link>
      </div>
      
      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}
      
      <Button
        type="submit"
        variant="primary"
        className="w-full"
        loading={isLoading}
      >
        Sign in
      </Button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t"></div>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="px-2 bg-background text-muted-foreground">Or continue with</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M23.745 12.27c0-.79-.07-1.54-.19-2.27h-11.3v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
          <path fill="#34A853" d="M12.255 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96h-3.98v3.09C3.515 21.3 7.565 24 12.255 24z"/>
          <path fill="#FBBC05" d="M5.525 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62h-3.98a11.86 11.86 0 000 10.76l3.98-3.09z"/>
          <path fill="#EA4335" d="M12.255 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C18.205 1.19 15.495 0 12.255 0c-4.69 0-8.74 2.7-10.71 6.62l3.98 3.09c.95-2.85 3.6-4.96 6.73-4.96z"/>
        </svg>
        Google (Coming soon)
      </Button>
    </form>
  );
}

function RegisterForm() {
  const register = useAuthStore(s => s.register);
  const isLoading = useAuthStore(s => s.isLoading);
  const error = useAuthStore(s => s.error);
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [orgName, setOrgName] = React.useState('');

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      useAuthStore.setState({ error: 'Passwords do not match' });
      return;
    }
    await register(email, password, fullName, orgName);
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <Input
        label="Full name"
        value={fullName}
        onChange={e => setFullName(e.target.value)}
        placeholder="John Doe"
        required
      />
      <Input
        label="Organization name"
        value={orgName}
        onChange={e => setOrgName(e.target.value)}
        placeholder="Your organization"
        required
      />
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="you@example.com"
        required
      />
      <Input
        label="Password"
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        placeholder="••••••••"
        minLength={8}
        required
        hint="Must be at least 8 characters"
      />
      <Input
        label="Confirm password"
        type="password"
        value={confirmPassword}
        onChange={e => setConfirmPassword(e.target.value)}
        placeholder="••••••••"
        minLength={8}
        required
      />
      
      {error && (
        <Alert variant="error">
          {error}
        </Alert>
      )}
      
      <Button
        type="submit"
        variant="secondary"
        className="w-full"
        loading={isLoading}
      >
        Create account
      </Button>

      <p className="text-xs text-muted-foreground text-center mt-4">
        We'll send you an email to verify your account
      </p>
    </form>
  );
}
